<template>
  <Combobox
    v-model="selectedItem"
    @update:model-value="itemAdded"
    :disabled="disabled"
    :immediate="true"
    as="div"
    v-slot="{ open }"
  >
    <div class="flex flex-col">
      <div
        class="rounded-t-large relative mt-2 flex flex-col"
        :class="[
          background,
          open && filteredItems.length > 0 && 'rounded-b-extra-small',
          (!open || filteredItems.length === 0) && 'rounded-b-large',
        ]"
        ref="inputFilter"
      >
        <BaseIcon
          v-if="leadingIcon"
          size="24"
          class="text-on-surface-variant absolute top-4.5 left-4"
          >{{ leadingIcon }}</BaseIcon
        >
        <ComboboxInput
          :disabled="disabled"
          class="peer rounded-t-large text-body-large border-box relative z-0 h-15.5 w-full border-[3px] border-transparent pt-3 outline-none focus:border-[3px]"
          :class="[
            error &&
              'border-error hover:border-on-error-container text-on-surface focus:border-error',
            !error &&
              'border-outline hover:border-on-surface text-on-surface-variant hover:text-on-surface focus:text-on-surface focus:border-primary',
            leadingIcon && 'pr-4 pl-13 focus:pl-[50px]',
            !leadingIcon && 'px-4 focus:px-[14px]',
            open && filteredItems.length > 0 && 'rounded-b-extra-small',
            (!open || filteredItems.length === 0) && 'rounded-b-large',
          ]"
          placeholder=" "
          @change="query = $event.target.value"
          @click="
            () => {
              if (!open) {
                openOptions()
              }
            }
          "
        />
        <label
          class="text-on-surface-variant text-body-small peer-placeholder-shown:text-body-large peer-focus:text-body-small pointer-events-none absolute top-1.5 bg-inherit px-1 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-focus:top-1.5 peer-focus:peer-placeholder-shown:top-1.5 peer-placeholder-shown:peer-focus:translate-y-0"
          :class="[
            error &&
              'peer-placeholder-shown:peer-focus:text-error peer-placeholder-shown:text-error peer-focus:text-error text-error peer-hover:text-on-error-container',
            !error &&
              'peer-placeholder-shown:peer-focus:text-primary peer-placeholder-shown:text-on-surface-variant peer-focus:text-primary peer-hover:text-on-surface',
            leadingIcon && 'left-3 peer-placeholder-shown:left-13 peer-focus:left-13',
            !leadingIcon && 'left-3 peer-placeholder-shown:left-3',
          ]"
        >
          {{ label }}
          {{ !hideDecorators ? (required ? '*' : `(${$t('common.optional')})`) : '' }}
        </label>
        <div
          v-if="hasActionsSlot && query.length === 0"
          class="absolute top-1/2 right-1 -translate-y-1/2"
        >
          <slot name="actions" />
        </div>
      </div>
      <p v-if="error" class="text-error text-body-small mt-1 px-4">
        {{ error }}
      </p>
    </div>
    <ComboboxButton ref="button" as="button" class="absolute h-0 w-0"></ComboboxButton>

    <Teleport to="#menu-container" defer>
      <ComboboxOptions
        ref="floatingEl"
        class="bg-surface-container rounded-t-extra-small rounded-b-large fixed z-80 mt-0.5 flex flex-col overflow-hidden shadow-md"
        :style="floatingStyles"
      >
        <ComboboxOption
          v-for="item in filteredItems"
          :key="item.id"
          v-slot="{ active }"
          :value="item"
          as="template"
        >
          <li
            class="border-outline-variant flex h-14 cursor-pointer flex-row items-center border-b px-4 py-2 last:border-none"
            :class="[active && 'bg-on-surface-variant/20']"
          >
            <slot :item="item" />
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </Teleport>
  </Combobox>
</template>

<script lang="ts" setup generic="T extends { id: string }">
  import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
  } from '@headlessui/vue'
  import { autoUpdate, flip, size, useFloating } from '@floating-ui/vue'
  import { computed, onMounted, ref, useTemplateRef, useSlots } from 'vue'
  import BaseIcon from '../icon/BaseIcon.vue'

  interface Props {
    label: string
    leadingIcon?: string
    error?: string
    placeholder?: string
    items: Array<T>
    queryFunction: (item: T, query: string) => boolean
    disabled?: boolean
    required?: boolean
    autofocus?: boolean
    hideDecorators?: boolean
    backgroundClass?:
      | 'surface'
      | 'container-low'
      | 'container'
      | 'container-high'
      | 'container-highest'
      | 'container-lowest'
  }

  const props = defineProps<Props>()

  const selectedItem = ref<T | undefined>()

  const inputFilter = useTemplateRef<HTMLElement>('inputFilter')
  const floatingEl = useTemplateRef<HTMLElement>('floatingEl')
  const button = useTemplateRef<{ el: HTMLButtonElement }>('button')

  const { floatingStyles, update } = useFloating(inputFilter, floatingEl, {
    strategy: 'fixed',
    placement: 'bottom-start',
    middleware: [
      flip({
        fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
      }),
      size({
        apply({ rects, elements }) {
          elements.floating.style.width = `${rects.reference.width}px`
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const itemAdded = () => {
    if (selectedItem.value) {
      emits('itemAdded', selectedItem.value)
      update()
    }
  }
  const emits = defineEmits<{
    (e: 'itemAdded', item: T): void
  }>()

  const query = ref('')

  const filteredItems = computed(() => {
    if (query.value === '') {
      return props.items.slice(0, 5)
    }
    return props.items.filter((item) => props.queryFunction(item, query.value)).slice(0, 10)
  })

  onMounted(() => {
    if (props.autofocus && inputFilter.value) {
      inputFilter.value.querySelector('input')?.focus()
    }
  })

  const openOptions = () => {
    if (button.value) {
      button.value.el.click()
    }
  }
  const slots = useSlots()
  const hasActionsSlot = computed(() => {
    return (slots.actions?.() ?? false) as boolean
  })

  const background = computed(() => {
    if (props.backgroundClass === 'container-low') {
      return 'bg-surface-container-low'
    } else if (props.backgroundClass === 'container') {
      return 'bg-surface-container'
    } else if (props.backgroundClass === 'container-high') {
      return 'bg-surface-container-high'
    } else if (props.backgroundClass === 'surface') {
      return 'bg-surface'
    } else if (props.backgroundClass === 'container-lowest') {
      return 'bg-surface-container-lowest'
    } else if (props.backgroundClass === 'container-highest') {
      return 'bg-surface-container-highest'
    }
    return 'bg-surface-container-low'
  })
</script>
